import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { Paging, SelectType } from '@wdx/clmi/api-services/models';
import { FilterQueryFacadeService } from '@wdx/clmi/api-services/state';
import {
    Document,
    FilterOperator,
    SystemEntity,
} from '@wdx/clmi/clmi-swagger-gen';
import { FilterHttpService } from '@wdx/shared/infrastructure/api-service';
import {
    FilePreview,
    FilesFacadeService,
} from '@wdx/shared/infrastructure/file-io';
import {
    TRANSLATION_BACK_BTN,
    TRANSLATION_NEXT_BTN,
    TRANSLATION_PLACEHOLDER_SEARCH_DOCUMENTS,
    TRANSLATION_SELECT_DOCUMENT,
    TRANSLATION_SVG_TEXT_NO_DOC_MATCHING,
    WdxDestroyClass,
} from '@wdx/shared/utils';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
    debounceTime,
    filter,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { MAX_PAGINATION_PAGE_SIZE } from '../../../../constants/api.constants';
import {
    ICON_COMPLETE,
    ICON_DOWNLOAD,
} from '../../../../constants/icons.constants';
import { ActionButtonMode } from '../../../../models/action-buttons-mode.model';
import { FeatureSvg } from '../../../../models/svg.model';
import { FiledDocumentsService } from '../../filed-documents/shared/services/filed-documents/filed-documents.service';
import { DocumentsFacadeService } from '../shared/services/documents-facade/documents-facade.service';
import { DocumentPreviewMode } from '../shared/services/documents-modal/documents-modal.model';

@Component({
    selector: 'clmi-document-preview',
    templateUrl: './document-preview.component.html',
    styleUrls: ['./document-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class DocumentPreviewComponent
    extends WdxDestroyClass
    implements OnInit, OnDestroy
{
    @Input() title: string;
    @Input() sourceType: SystemEntity;
    @Input() activeDocument: Document;
    @Input() documents: Document[];
    @Input() entityId: string;
    @Input() entityType: SystemEntity;
    @Input() mode: DocumentPreviewMode = DocumentPreviewMode.SearchAndPreview;
    @Input() useModal = true;
    @Input() hideFileList = false;
    @Input() permittedTypes: string[];
    @Input() linkToId: string;
    @Input() linkedIds: string[];
    @Input() hideCloseModal: boolean;
    @Input() queryEntity: string;

    @Output() modalClosed = new EventEmitter();

    @ViewChildren('menuItem') menuItems!: QueryList<ElementRef>;

    readonly FEATURE_SVG = FeatureSvg;
    readonly ICON_COMPLETE = ICON_COMPLETE.icon;
    readonly ICON_DOWNLOAD = ICON_DOWNLOAD.icon;
    readonly ACTION_BUTTON_MODE = ActionButtonMode;
    readonly DOCUMENT_PREVIEW_MODE = DocumentPreviewMode;
    readonly BACK_BTN = TRANSLATION_BACK_BTN;
    readonly NEXT_BTN = TRANSLATION_NEXT_BTN;
    readonly PLACEHOLDER_SEARCH_DOCUMENTS =
        TRANSLATION_PLACEHOLDER_SEARCH_DOCUMENTS;
    readonly SELECT_DOCUMENT = TRANSLATION_SELECT_DOCUMENT;
    readonly NO_DOC_MATCHING = TRANSLATION_SVG_TEXT_NO_DOC_MATCHING;

    activeDocument$ = new BehaviorSubject<Document>(null);
    documents$ = new BehaviorSubject<Document[]>(null);
    isLoading$: Observable<boolean>;
    hasError$: Observable<boolean>;

    filePreview$: Observable<FilePreview>;
    filePreviewIsLoading$: Observable<boolean>;
    filePreviewHasError$: Observable<boolean>;

    paging: Paging;
    searchText: string;
    searchTextChanged$ = new Subject<string>();

    get totalRecords() {
        if (this.mode === DocumentPreviewMode.PreviewOnly) {
            return this.documents?.length || '--';
        }
        return !isNaN(this.paging?.totalRecords)
            ? this.paging?.totalRecords
            : '--';
    }

    private filterHttpService = inject(FilterHttpService);
    private documentsFacadeService = inject(DocumentsFacadeService);
    private filesFacadeService = inject(FilesFacadeService);
    private filedDocumentsService = inject(FiledDocumentsService);
    private filterQueryFacadeService = inject(FilterQueryFacadeService);

    ngOnInit(): void {
        this.activeDocument$.next(this.activeDocument);

        this.activeDocument$
            .pipe(filter((document) => Boolean(document)))
            .subscribe((document) => {
                this.queryEntity
                    ? this.setContextualPreview(
                          this.queryEntity,
                          this.entityId,
                          document,
                      )
                    : this.setFilePreview(document);
            });

        if (this.mode === DocumentPreviewMode.PreviewOnly) {
            this.documents$.next(this.documents);
            return;
        }

        this.isLoading$ = this.filterQueryFacadeService.selectFilterLoading$(
            this.getSelectType(),
        );

        this.hasError$ = this.filterQueryFacadeService.selectFilterError$(
            this.getSelectType(),
        );

        this.searchTextChanged$
            .pipe(
                debounceTime(200),
                takeUntil(this.destroyed$),
                switchMap((text) => {
                    this.activeDocument$.next(null);
                    return this.loadDataAndUpdate(text);
                }),
            )
            .subscribe();

        this.loadDataAndUpdate().pipe(takeUntil(this.destroyed$)).subscribe();
    }

    setFilePreview(document: Document) {
        this.filePreview$ = this.filesFacadeService.getFilePreview$(
            document.fileIndex,
        );
        this.filePreviewIsLoading$ =
            this.filesFacadeService.getFilePreviewIsLoading$(
                document.fileIndex,
            );
        this.filePreviewHasError$ =
            this.filesFacadeService.getFilePreviewHasError$(document.fileIndex);
    }

    setContextualPreview(
        queryEntity: string,
        entityId: string,
        document: Document,
    ) {
        this.filePreview$ = this.filesFacadeService.getContextualFilePreview$(
            queryEntity,
            entityId,
            document.fileIndex?.contentType,
        );
    }

    getQuery(searchText = '') {
        return {
            expressions: [
                {
                    field: 'Search',
                    operator: FilterOperator.Matches,
                    values: [searchText],
                },
                this.filedDocumentsService.getContextual(
                    this.entityType,
                    this.entityId,
                ),
                ...(this.permittedTypes
                    ? [
                          {
                              field: 'Type',
                              operator: FilterOperator.In,
                              values: this.permittedTypes,
                          },
                      ]
                    : []),
            ],
        };
    }

    getSelectType(): SelectType {
        return `${this.sourceType}Preview` as SelectType;
    }

    getActiveDocumentIndex(): number {
        if (!this.activeDocument$.getValue()) {
            return -1;
        }
        return this.documents$
            .getValue()
            .findIndex(
                (document) =>
                    document.id === this.activeDocument$.getValue().id,
            );
    }

    scrollMenuItemIntoView(goToIndex: number) {
        this.menuItems.get(goToIndex)?.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
        });
    }

    onDownloadDocument(): void {
        this.filesFacadeService.downloadFile(
            this.activeDocument$.getValue().fileIndex,
        );
    }

    onSearch(text: string): void {
        this.searchTextChanged$.next(text);
    }

    onSelect(document: Document): void {
        this.activeDocument$.next(document);
    }

    onBack(): void {
        const goToIndex = this.getActiveDocumentIndex() - 1;
        this.activeDocument$.next(this.documents$.getValue()[goToIndex]);
        this.scrollMenuItemIntoView(goToIndex);
    }

    onNext(): void {
        const goToIndex = this.getActiveDocumentIndex() + 1;
        this.activeDocument$.next(this.documents$.getValue()[goToIndex]);
        this.scrollMenuItemIntoView(goToIndex);
    }

    onLinkToggled(isLinked: boolean, documentId: string): void {
        if (isLinked) {
            this.linkedIds.push(documentId);
        } else {
            this.linkedIds = this.linkedIds.filter((id) => id !== documentId);
        }
        this.documentsFacadeService.linkDocumentToRequirement(
            this.linkToId,
            documentId,
            isLinked,
        );
    }

    onInfinityScrollFired(): void {
        if (this.paging.page < this.paging.totalPages) {
            this.filterQueryFacadeService.updateResults(
                this.sourceType,
                this.paging,
                this.getQuery(),
                this.getSelectType(),
            );
        }
    }

    ngOnDestroy(): void {
        this.filterQueryFacadeService.resetResults(
            this.sourceType,
            this.getSelectType(),
        );
        this.filesFacadeService.destroyFilePreviews();
        super.ngOnDestroy();
    }

    toggleFileList() {
        this.hideFileList = !this.hideFileList;
    }

    loadData(searchText = '') {
        return this.filterHttpService.getRecords(
            this.sourceType,
            { pageSize: MAX_PAGINATION_PAGE_SIZE },
            this.getQuery(searchText),
        );
    }

    loadDataAndUpdate(searchText?: string) {
        return this.loadData(searchText).pipe(
            tap((data) => {
                this.documents$.next(data.results);
                this.paging = data.paging;
            }),
        );
    }
}
